// Supabase store: anon inserts, token-gated owner mutations via RPCs,
// media in the board-media bucket. Will be superseded by the
// Cloudflare Worker API store; both sit behind the same interface.

import type { Note, NoteUpdate } from "@ts/core/types.ts";
import type { BoardEvent, MediaChange, NewNote, Store } from "@ts/store/store.ts";
import { uuid } from "@ts/core/uuid.ts";

export class SupabaseStore implements Store {
  readonly remote = true;
  private db: any = null;
  private makeClient: ((word: string | null) => any) | null = null;
  private channel: any = null;

  constructor(
    private url: string,
    private anonKey: string,
    private roomWord: string | null = null,
    /** null = the legacy Old Friend board (notes with board_id IS NULL). */
    private boardId: string | null = null
  ) {}

  /** Attach a room word mid-session — no page reload, so whatever the
   *  person was writing (text, photo, recording) survives. */
  setRoomWord(word: string): void {
    this.roomWord = word;
    // The live channel keeps running on the old client; reads are open.
    if (this.makeClient) this.db = this.makeClient(word);
  }

  async init(): Promise<Date | null> {
    const { createClient } = await import(
      /* @vite-ignore */ "https://esm.sh/@supabase/supabase-js@2" as string
    );
    this.makeClient = (word: string | null) =>
      createClient(
        this.url,
        this.anonKey,
        word ? { global: { headers: { "x-room-word": word } } } : undefined
      );
    this.db = this.makeClient(this.roomWord);
    // Multi-box boards carry their own freeze date (already in config);
    // only the legacy board reads the single board_settings row.
    if (this.boardId) return null;
    const { data } = await this.db.from("board_settings").select("freeze_at").single();
    return data?.freeze_at ? new Date(data.freeze_at) : null;
  }

  private mediaUrl(path: string): string {
    return this.db.storage.from("board-media").getPublicUrl(path).data.publicUrl;
  }

  private withUrls(n: any): Note {
    return {
      ...n,
      photo_url: n.photo_path ? this.mediaUrl(n.photo_path) : null,
      audio_url: n.audio_path ? this.mediaUrl(n.audio_path) : null,
    };
  }

  private async upload(blob: Blob, kind: "photo" | "audio"): Promise<string> {
    const stamp = Date.now() + "-" + Math.random().toString(36).slice(2, 8);
    const ext = kind === "photo" ? "jpg" : blob.type.includes("mp4") ? "m4a" : "webm";
    const dir = kind === "photo" ? "photos" : "audio";
    // Legacy board keeps its flat path (photos/<file>); new boards nest
    // under the board id (photos/<board_id>/<file>) to match the RLS
    // upload policy without touching the legacy one.
    const path = this.boardId ? `${dir}/${this.boardId}/${stamp}.${ext}` : `${dir}/${stamp}.${ext}`;
    const { error } = await this.db.storage.from("board-media").upload(path, blob, {
      contentType: kind === "photo" ? "image/jpeg" : blob.type,
    });
    if (error) throw error;
    return path;
  }

  async list(): Promise<Note[]> {
    const base = () => this.db.from("notes").select("*").order("created_at", { ascending: true });
    let res;
    if (this.boardId) {
      res = await base().eq("board_id", this.boardId);
    } else {
      res = await base().is("board_id", null);
      // pre-migration the board_id column doesn't exist yet — fall back
      if (res.error) res = await base();
    }
    if (res.error) throw res.error;
    return res.data.map((n: any) => this.withUrls(n));
  }

  async create(note: NewNote, media: MediaChange) {
    // Omit board_id on the legacy board so this works even before the
    // multi-box migration adds the column.
    const row: any = { ...note };
    if (this.boardId) row.board_id = this.boardId;
    if (media.photo) row.photo_path = await this.upload(media.photo, "photo");
    if (media.audio) row.audio_path = await this.upload(media.audio, "audio");
    const { data, error } = await this.db.from("notes").insert(row).select().single();
    if (error) throw error;
    const token = uuid();
    await this.db.from("note_tokens").insert({ note_id: data.id, token });
    return { note: this.withUrls(data), token };
  }

  async update(id: string, token: string | null, fields: NoteUpdate, media: MediaChange) {
    const patch: Partial<Note> & { photo_path?: string; audio_path?: string } = { ...fields };
    let photoPath: string | null | undefined;
    let audioPath: string | null | undefined;
    if (media.photo) photoPath = await this.upload(media.photo, "photo");
    else if (media.removePhoto) photoPath = "";
    if (media.audio) audioPath = await this.upload(media.audio, "audio");
    else if (media.removeAudio) audioPath = "";

    const { error } = await this.db.rpc("update_own_note", {
      p_note_id: id,
      p_token: token,
      p_pos_x: fields.pos_x ?? null,
      p_pos_y: fields.pos_y ?? null,
      p_pin: fields.pin ?? null,
      p_message: fields.message ?? null,
      p_name: fields.name ?? null,
      p_paper: fields.paper ?? null,
      p_font: fields.font ?? null,
      p_ink: fields.ink ?? null,
      p_photo_path: photoPath ?? null,
      p_audio_path: audioPath ?? null,
    });
    if (error) throw error;

    if (fields.name !== undefined) patch.name = fields.name || null;
    if (photoPath !== undefined) patch.photo_url = photoPath ? this.mediaUrl(photoPath) : null;
    if (audioPath !== undefined) patch.audio_url = audioPath ? this.mediaUrl(audioPath) : null;
    return patch;
  }

  async remove(id: string, token: string | null) {
    const { error } = await this.db.rpc("delete_own_note", { p_note_id: id, p_token: token });
    if (error) throw error;
  }

  async saveEmail(email: string) {
    const row: any = { email };
    if (this.boardId) row.board_id = this.boardId;
    await this.db.from("keepsake_emails").insert(row);
  }

  track(visitor: string, name: string, props?: Record<string, unknown>) {
    const row: any = { visitor, name, props: props ?? null };
    if (this.boardId) row.board_id = this.boardId;
    this.db?.from("events").insert(row).then(
      () => {},
      () => {} // analytics must never break the experience
    );
  }

  subscribe(onEvent: (e: BoardEvent) => void, onPresence?: (count: number) => void) {
    const channel = this.db.channel(`board:${this.boardId ?? "legacy"}`, {
      config: { presence: { key: uuid() } },
    });
    this.channel = channel; // keep alive across client swaps
    channel
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notes" },
        (payload: any) => {
          // only this board's notes (legacy board = board_id null)
          const row = payload.new ?? payload.old;
          const rowBoard = row?.board_id ?? null;
          if (rowBoard !== this.boardId) return;
          if (payload.eventType === "DELETE") {
            onEvent({ type: "delete", note: { id: payload.old.id } as Note });
          } else if (payload.new.hidden) {
            onEvent({ type: "delete", note: payload.new });
          } else {
            onEvent({ type: "upsert", note: this.withUrls(payload.new) });
          }
        }
      )
      .on("presence", { event: "sync" }, () => {
        onPresence?.(Object.keys(channel.presenceState()).length);
      })
      .subscribe(async (status: string) => {
        if (status === "SUBSCRIBED") await channel.track({ here: true });
      });
  }
}
